import { useState, useRef, useEffect } from 'react';

import { motion } from 'framer-motion';
import { useParams } from 'react-router-dom';

import { FactSubsetRelevance } from '../../../../../../../../../../../backend/src/matter/fact/fact-subset-relevance.entity';
import { DocChronologyDoc } from '../../../../../../../../../../../backend/src/matter/subset/chronology/doc-chronology/doc-chronology-doc.entity';
import { useUpdateDocChronologyDocFacts } from '@/api/mutations/useUpdateDocChronologyDocFacts';
import { relevanceIcons, relevanceMapping } from '@/helpers/filterNames';
import { Chip } from '@/new/components/chip';
import { Icon } from '@/new/components/icon';
import { ProcessingChip, ProcessingState } from '@/new/components/processing-chip';
import { Button } from '@/new/components/shadcn/button';
import { CHIP_COLOURS } from '@/new/lib/chip-colours';
import { cn } from '@/new/lib/utils';

const COLLAPSED_HEIGHT = 43; // 2 lines of text

export function DocChronRowFact({
  fact,
  chronDoc,
  isExpanded,
}: {
  fact: FactSubsetRelevance;
  chronDoc: DocChronologyDoc;
  isExpanded: boolean;
}) {
  const { matterId, subsetId, docChronologyId } = useParams();
  const contentRef = useRef<HTMLParagraphElement>(null);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const [contentHeight, setContentHeight] = useState(COLLAPSED_HEIGHT);

  const { mutate: updateFacts } = useUpdateDocChronologyDocFacts();

  const handleRemoveFact = () => {
    if (!matterId || !subsetId || !docChronologyId) return;

    const remainingFactIds = chronDoc.facts.filter((f) => f.id !== fact.id).map((f) => f.fact.id);

    updateFacts({
      matterId,
      subsetId,
      docChronologyId,
      docId: chronDoc.id,
      factIds: remainingFactIds,
    });
  };

  useEffect(() => {
    if (contentRef.current) {
      const height = contentRef.current.scrollHeight;
      setContentHeight(height);
      setIsOverflowing(height > COLLAPSED_HEIGHT);
    }
  }, [fact.fact.description]);

  return (
    <motion.div
      layoutId={`fact-card-${fact.id}`}
      layout
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.1 }}
      className="relative max-w-xl min-w-xl rounded-md border bg-white p-2.5 px-2.75 shadow-lg/2"
    >
      {/* Remove fact button */}
      <Button
        onClick={handleRemoveFact}
        className={cn(
          'absolute -top-2 -right-2 rounded-full px-0.5 py-0.5 text-[11px] transition-[opacity,border-color] duration-100',
          isExpanded ? 'opacity-100' : 'opacity-0',
        )}
      >
        <Icon className="lucide--x" />
      </Button>

      {/* Description - animates height smoothly */}
      <div className="relative">
        <div className="mb-1.5 flex items-center gap-1.5">
          <p className={`${fact.fact.factDateText ? 'font-medium' : 'text-gray-500'} -mb-0.75 truncate px-1 pl-1.25`}>
            {fact.fact.factDateText
              ? fact.fact.factDateText.charAt(0).toUpperCase() + fact.fact.factDateText.slice(1)
              : 'No Date'}
          </p>

          {fact.isKey && (
            <Chip className={cn(CHIP_COLOURS.key, 'mr-0.5 border')}>
              <Icon className="lucide--key-round" />
              <span>Key</span>
            </Chip>
          )}

          {fact.relevance ? (
            <Chip className={cn(CHIP_COLOURS.relevance[fact.relevance], 'border')}>
              <Icon className={relevanceIcons[fact.relevance]} />
              {relevanceMapping[fact.relevance]}
            </Chip>
          ) : (
            <ProcessingChip processingState={ProcessingState.Processing} processingText="Processing Relevance" />
          )}
        </div>

        <motion.div
          className="mx-1 overflow-hidden"
          animate={{ maxHeight: isExpanded ? contentHeight : COLLAPSED_HEIGHT }}
          transition={{ duration: 0.2, ease: 'easeOut' }}
          initial={false}
        >
          <p ref={contentRef} className="text-gray-700">
            {fact.fact.description}
          </p>
        </motion.div>

        {/* Gradient fade overlay - only show if overflowing */}
        {isOverflowing && (
          <div
            className={cn(
              'pointer-events-none absolute inset-x-0 -bottom-0.5 h-8 bg-gradient-to-t from-white/50 to-transparent transition-opacity duration-50',
              isExpanded ? 'opacity-0' : 'opacity-100',
            )}
          />
        )}
      </div>

      {/* Additional info - smoothly revealed on hover */}
      <motion.div
        className="overflow-hidden px-1"
        animate={{ height: isExpanded ? 'auto' : 0 }}
        transition={{ duration: 0.15, ease: 'easeOut' }}
        initial={false}
      >
        <div className="space-y-2 px-0.5 pt-3 pb-2">
          <p className="border-l-2 border-gray-200/80 py-1 pl-3.5 text-gray-800 italic">
            "{fact.fact.locations.find((fl) => fl.docId === chronDoc.docId)?.sources?.map((source) => source.text)}"
          </p>
        </div>
      </motion.div>
    </motion.div>
  );
}
